/**
 * Shared timber take-off utilities — board feet, dimensions, proposal save.
 */

import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getProposalRates } from './appSettings';
import type {
  PdfMeasurement,
  PdfMeasurementCalibration,
  PdfTakeoffLink,
  PdfTakeoffStructure,
  ProposalBuilderData,
  ProposalNodeSet,
  TimberListItem,
} from '../types';
import { createDefaultNodeSet, DEFAULT_PROPOSAL_CHECKLIST } from '../types';
import { designIncludedInKitForNewProposalWrite } from './proposalCalculations';
import { applyCalibration, convertLength, normToPdf, polylineLength, segmentLength } from './measurementMath';

/** Normalize cross-section to smaller×larger×length (e.g. 8x6x8 → 6x8x8). */
export function standardizeTimberDimensions(dimensions: string): string {
  const s = (dimensions || '').trim();
  const match = s.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return s;
  const a = parseFloat(match[1]);
  const b = parseFloat(match[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return s;
  if (a <= b) return `${match[1]}x${match[2]}x${match[3]}`;
  return `${match[2]}x${match[1]}x${match[3]}`;
}

export function parseDimensions(dimensions: string): number {
  const s = standardizeTimberDimensions(dimensions || '');
  const match = s.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return 0;
  const height = parseFloat(match[1]);
  const width = parseFloat(match[2]);
  const length = parseFloat(match[3]);
  return (height * width * length) / 12;
}

export function getBoardFeet(item: TimberListItem): number {
  return parseDimensions(item.dimensions) || item.boardFeet || 0;
}

export function createEmptyTimberItem(structureId?: string, structureName?: string): TimberListItem {
  return {
    id: `timber-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    structureId,
    structureName,
    name: '',
    species: '',
    grade: '',
    dimensions: '',
    moistureContent: '',
    quantity: 1,
    boardFeet: 0,
    unitCost: 0,
    total: 0,
  };
}

export function composeDimensions(height: string, width: string, length: string): string {
  const h = height.trim();
  const w = width.trim();
  const l = length.trim();
  if (!h || !w || !l) return '';
  return standardizeTimberDimensions(`${h}x${w}x${l}`);
}

export function parseDimensionsParts(dimensions: string): { height: string; width: string; length: string } {
  const s = standardizeTimberDimensions(dimensions || '');
  const match = s.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return { height: '', width: '', length: '' };
  return { height: match[1], width: match[2], length: match[3] };
}

/** Cross-section for display (inches) — smaller × larger. */
export function formatCrossSection(dimensions: string): string {
  const { height, width } = parseDimensionsParts(dimensions);
  if (!width || !height) return '';
  return `${height}×${width}"`;
}

/** Cross-section only (smaller×larger) without length — for take-off tables/exports. */
export function formatCrossSectionDims(dimensions: string): string {
  const { height, width } = parseDimensionsParts(dimensions);
  if (!width || !height) return '';
  return `${height}x${width}`;
}

/** Timber order length in feet from dimensions string (third H×W×L component). */
export function formatTimberOrderLength(dimensions: string): string {
  const { length } = parseDimensionsParts(dimensions);
  if (!length) return '';
  return `${length}'`;
}

export function formatTimberCurrency(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);
}

/** Real-world length in feet from a linear or polyline measurement. */
export function computeMeasurementLengthFt(
  measurement: Pick<PdfMeasurement, 'kind' | 'points'>,
  calibration: PdfMeasurementCalibration | null,
  pageNaturalWidth: number,
  pageNaturalHeight: number,
): number | null {
  if (!calibration) return null;
  const pdfPts = measurement.points.map(p => normToPdf(p, pageNaturalWidth, pageNaturalHeight));
  if (measurement.kind === 'linear' && pdfPts.length >= 2) {
    const d = segmentLength(pdfPts[0], pdfPts[1]);
    const real = applyCalibration(d, calibration);
    return convertLength(real, calibration.baseUnit, 'ft');
  }
  if (measurement.kind === 'polyline' && pdfPts.length >= 2) {
    const d = polylineLength(pdfPts);
    const real = applyCalibration(d, calibration);
    return convertLength(real, calibration.baseUnit, 'ft');
  }
  return null;
}

/** Round feet to a sensible timber length (nearest 1/4 ft) for display of raw measurements. */
export function roundTimberLengthFt(ft: number): number {
  return Math.round(ft * 4) / 4;
}

/** Normalized midpoint of a measurement (0–100 coords) for on-page structure badges. */
export function measurementNormMidpoint(
  m: Pick<PdfMeasurement, 'kind' | 'points'>,
): { x: number; y: number } | null {
  if (m.points.length < 2) return null;
  if (m.kind === 'linear') {
    const [a, b] = m.points;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
  let sumX = 0;
  let sumY = 0;
  for (const p of m.points) {
    sumX += p.x;
    sumY += p.y;
  }
  return { x: sumX / m.points.length, y: sumY / m.points.length };
}

/** Nearest linked take-off on the same page (by measurement midpoint distance). */
export function inferNearestTakeoffLinkForMeasurement(
  measurement: PdfMeasurement,
  links: PdfTakeoffLink[],
  measurements: PdfMeasurement[],
): PdfTakeoffLink | null {
  const targetMid = measurementNormMidpoint(measurement);
  if (!targetMid) return null;

  const measurementById = new Map<string, PdfMeasurement>();
  for (const m of measurements) {
    if (m.id) measurementById.set(m.id, m);
  }

  let bestLink: PdfTakeoffLink | null = null;
  let bestDist = Infinity;

  for (const link of links) {
    if (link.pageNumber !== measurement.pageNumber) continue;
    const linked = measurementById.get(link.measurementId);
    if (!linked) continue;
    const mid = measurementNormMidpoint(linked);
    if (!mid) continue;
    const dist = Math.hypot(mid.x - targetMid.x, mid.y - targetMid.y);
    if (dist < bestDist) {
      bestDist = dist;
      bestLink = link;
    }
  }

  return bestLink;
}

/** Pick a structure for a new take-off link — pin/active override, then spatial nearest. */
export function resolveInitialTakeoffStructureId(
  measurement: PdfMeasurement,
  links: PdfTakeoffLink[],
  measurements: PdfMeasurement[],
  options?: {
    pinnedStructureId?: string | null;
    activeStructureId?: string | null;
  },
): string | null {
  const pinned = options?.pinnedStructureId?.trim();
  if (pinned) return pinned;

  const nearest = inferNearestTakeoffLinkForMeasurement(measurement, links, measurements);
  if (nearest?.structureId) return nearest.structureId;

  const active = options?.activeStructureId?.trim();
  if (active) return active;

  if (links.length > 0) {
    const sorted = [...links].sort(
      (a, b) =>
        (b.updatedAt?.getTime() ?? b.createdAt.getTime()) -
        (a.updatedAt?.getTime() ?? a.createdAt.getTime()),
    );
    return sorted[0]?.structureId ?? null;
  }

  return null;
}

/** Default timber fields from an existing link (cross-section, species, grade, etc.). */
export function timberDefaultsFromTakeoffLink(link: PdfTakeoffLink): {
  species: string;
  grade: string;
  crossHeight: string;
  crossWidth: string;
  moistureContent: '' | 'green' | 'kiln_dried';
  unitCost: number;
} {
  const parts = parseDimensionsParts(link.dimensions);
  return {
    species: link.species || '',
    grade: link.grade || '',
    crossHeight: parts.height,
    crossWidth: parts.width,
    moistureContent: link.moistureContent ?? '',
    unitCost: link.unitCost ?? 0,
  };
}

const MIN_TIMBER_ORDER_LENGTH_FT = 8;

/**
 * Default order length from a measured run: round up to the next even whole foot,
 * then apply the 8' supplier minimum (whichever is greater).
 */
export function defaultTimberOrderLengthFt(measuredFt: number): number {
  const ceiled = Math.ceil(measuredFt);
  const evenCeiled = ceiled % 2 === 0 ? ceiled : ceiled + 1;
  return Math.max(evenCeiled, MIN_TIMBER_ORDER_LENGTH_FT);
}

export function timberItemFromLinkFields(fields: {
  structureId: string;
  structureName: string;
  name: string;
  species: string;
  grade: string;
  dimensions: string;
  quantity: number;
  unitCost: number;
  moistureContent?: '' | 'green' | 'kiln_dried';
  sourceMeasurementId?: string;
  sourceDocumentId?: string;
}): TimberListItem {
  const dimensions = standardizeTimberDimensions(fields.dimensions);
  const boardFeet = parseDimensions(dimensions);
  return {
    id: `timber-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    structureId: fields.structureId,
    structureName: fields.structureName,
    name: fields.name,
    species: fields.species,
    grade: fields.grade,
    dimensions,
    moistureContent: fields.moistureContent ?? '',
    quantity: fields.quantity,
    boardFeet,
    unitCost: fields.unitCost,
    total: fields.unitCost * fields.quantity,
    sourceMeasurementId: fields.sourceMeasurementId,
    sourceDocumentId: fields.sourceDocumentId,
  };
}

export interface SaveTakeoffResult {
  ok: boolean;
  message: string;
}

export interface TakeoffStructureOption {
  id: string;
  name: string;
  source: 'draft' | 'proposal';
}

export function mergeTakeoffStructureOptions(
  drafts: PdfTakeoffStructure[],
  proposalNodeSets: ProposalNodeSet[],
): TakeoffStructureOption[] {
  const seen = new Set<string>();
  const out: TakeoffStructureOption[] = [];
  for (const d of drafts) {
    if (!d.id || seen.has(d.id)) continue;
    seen.add(d.id);
    out.push({ id: d.id, name: d.name, source: 'draft' });
  }
  for (const ns of proposalNodeSets) {
    if (!ns.id || seen.has(ns.id)) continue;
    seen.add(ns.id);
    out.push({ id: ns.id, name: ns.name, source: 'proposal' });
  }
  return out;
}

function slugifyPart(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function generateAccessPin(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

function applyTimberListToNodeSet(
  ns: ProposalNodeSet,
  timberList: TimberListItem[],
  productionRatePerBdft: number,
): ProposalNodeSet {
  const timberTotalVal = timberList.reduce((s, i) => s + i.total, 0);
  const totalBoardFeetVal = timberList.reduce((s, i) => s + getBoardFeet(i) * i.quantity, 0);
  const materialsTotal =
    timberTotalVal +
    (ns.materials.pegsCost || 0) +
    (ns.materials.postBasesTotal || 0) +
    (ns.materials.fastenerPackageCost || 0) +
    (ns.materials.deckingTotal || 0);
  return {
    ...ns,
    materials: {
      ...ns.materials,
      timberList,
      timberTotal: timberTotalVal,
      totalBoardFeet: totalBoardFeetVal,
      total: materialsTotal,
    },
    production: {
      ...ns.production,
      boardFeet: totalBoardFeetVal,
      total: totalBoardFeetVal * productionRatePerBdft,
    },
  };
}

/**
 * Create or update proposal from take-off: merges draft structures, applies timber
 * only to structures that have take-off items (preserves other structures).
 */
export async function pushTakeoffToProposal(
  dealId: string,
  leadId: string,
  items: TimberListItem[],
  draftStructures: PdfTakeoffStructure[],
  meta?: { userId?: string; contactName?: string; projectName?: string },
): Promise<SaveTakeoffResult> {
  if (items.length === 0) {
    return { ok: false, message: 'No take-off items to push' };
  }

  const ref = doc(db, 'proposalBuilders', dealId);
  const snap = await getDoc(ref);
  const rates = await getProposalRates();
  const productionRate = rates.productionRatePerBdft;

  let data: ProposalBuilderData;
  const isNew = !snap.exists();

  if (isNew) {
    const contact = slugifyPart(meta?.contactName || 'client');
    const project = slugifyPart(meta?.projectName || 'project');
    const slugBase = [contact, project].filter(Boolean).join('-') || `deal-${dealId.slice(0, 8)}`;
    data = {
      dealId,
      slug: slugBase.replace(/-+/g, '-').slice(0, 80),
      accessPin: generateAccessPin(),
      nodeSets: [],
      profitMargin: rates.defaultProfitMargin || 40,
      status: 'draft',
      checklist: DEFAULT_PROPOSAL_CHECKLIST,
      designIncludedInKit: designIncludedInKitForNewProposalWrite(),
      designTotal: 0,
      timberKitTotal: 0,
      installTotal: 0,
      rawCostTotal: 0,
      billableTotal: 0,
      grossProfit: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: meta?.userId || 'takeoff',
    };
  } else {
    data = snap.data() as ProposalBuilderData;
  }

  const nodeSetMap = new Map<string, ProposalNodeSet>();
  for (const ns of data.nodeSets || []) {
    nodeSetMap.set(ns.id, ns);
  }
  for (const draft of draftStructures) {
    if (draft.id && !nodeSetMap.has(draft.id)) {
      nodeSetMap.set(draft.id, createDefaultNodeSet(draft.name, draft.id));
    }
  }

  const byStructure = new Map<string, TimberListItem[]>();
  for (const item of items) {
    let sid = item.structureId;
    if (!sid && item.structureName) {
      const draftMatch = draftStructures.find(
        d => d.name?.toLowerCase() === item.structureName?.toLowerCase(),
      );
      sid = draftMatch?.id;
    }
    if (!sid) continue;

    if (!nodeSetMap.has(sid)) {
      const draft = draftStructures.find(d => d.id === sid);
      nodeSetMap.set(
        sid,
        createDefaultNodeSet(draft?.name || item.structureName || 'Structure', sid),
      );
    }

    const bf = getBoardFeet(item);
    if (!byStructure.has(sid)) byStructure.set(sid, []);
    const ns = nodeSetMap.get(sid)!;
    byStructure.get(sid)!.push({
      ...item,
      id: item.id || `timber-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      dimensions: standardizeTimberDimensions(item.dimensions),
      boardFeet: bf,
      structureId: sid,
      structureName: ns.name || item.structureName,
    });
  }

  if (byStructure.size === 0) {
    return { ok: false, message: 'No structures matched take-off items' };
  }

  const nodeSetsUpdated = Array.from(nodeSetMap.values()).map(ns => {
    const timberList = byStructure.get(ns.id);
    if (!timberList) return ns;
    return applyTimberListToNodeSet(ns, timberList, productionRate);
  });

  const proposalDoc: ProposalBuilderData = {
    ...data,
    nodeSets: nodeSetsUpdated,
    updatedAt: new Date(),
  };

  await setDoc(ref, proposalDoc, { merge: true });

  return {
    ok: true,
    message: isNew ? 'Created proposal from take-off' : 'Pushed take-off to proposal',
  };
}

/**
 * Batch-save take-off items grouped by structure into proposalBuilders.
 * Replaces timber lists only for structures included in `items`.
 */
export async function saveTakeoffItemsToProposal(
  dealId: string,
  items: TimberListItem[],
  defaultStructureId?: string | null,
  draftStructures: PdfTakeoffStructure[] = [],
  meta?: { userId?: string; leadId?: string; contactName?: string; projectName?: string },
): Promise<SaveTakeoffResult> {
  const ref = doc(db, 'proposalBuilders', dealId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    if (!meta?.leadId) {
      return { ok: false, message: 'Proposal not found' };
    }
    return pushTakeoffToProposal(dealId, meta.leadId, items, draftStructures, meta);
  }

  const data = snap.data() as ProposalBuilderData;
  const rates = await getProposalRates();
  const sets = [...(data.nodeSets || [])];

  const nodeSetMap = new Map<string, ProposalNodeSet>(sets.map(ns => [ns.id, ns]));
  for (const draft of draftStructures) {
    if (draft.id && !nodeSetMap.has(draft.id)) {
      nodeSetMap.set(draft.id, createDefaultNodeSet(draft.name, draft.id));
    }
  }

  const byStructure = new Map<string, TimberListItem[]>();
  for (const item of items) {
    let sid: string | undefined = item.structureId || defaultStructureId || undefined;
    if (!sid && item.structureName) {
      const draftMatch = draftStructures.find(
        d => d.name?.toLowerCase() === item.structureName?.toLowerCase(),
      );
      sid = draftMatch?.id || sets.find(ns => ns.name?.toLowerCase() === item.structureName?.toLowerCase())?.id;
    }
    if (!sid) sid = sets[0]?.id;
    if (!sid) continue;

    if (!nodeSetMap.has(sid)) {
      const draft = draftStructures.find(d => d.id === sid);
      nodeSetMap.set(sid, createDefaultNodeSet(draft?.name || item.structureName || 'Structure', sid));
    }

    const bf = getBoardFeet(item);
    if (!byStructure.has(sid)) byStructure.set(sid, []);
    const ns = nodeSetMap.get(sid)!;
    byStructure.get(sid)!.push({
      ...item,
      id: item.id || `timber-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      dimensions: standardizeTimberDimensions(item.dimensions),
      boardFeet: bf,
      structureId: sid,
      structureName: ns.name || item.structureName,
    });
  }

  const nodeSetsUpdated = Array.from(nodeSetMap.values()).map(ns => {
    const timberList = byStructure.get(ns.id);
    if (!timberList) return ns;
    return applyTimberListToNodeSet(ns, timberList, rates.productionRatePerBdft);
  });

  await setDoc(ref, { nodeSets: nodeSetsUpdated, updatedAt: new Date() }, { merge: true });
  return { ok: true, message: 'Saved to structures' };
}
